/**
 * API client for the Forge Trainer Portal
 * Uses token-based authentication with session storage
 */

// Derive the WebObjects base path from current URL
function getBasePath() {
  const path = window.location.pathname
  const match = path.match(/^(.*\/WebObjects\/forge\.woa)/)
  return match ? match[1] : ''
}

const WO_BASE = getBasePath()
const API_BASE = `${WO_BASE}/wa/appApi`
const REST_BASE = `${WO_BASE}/ra`

/**
 * Custom API error class for better error handling
 */
export class ApiError extends Error {
  constructor(message, status, code = null, data = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.data = data
    this.isNetworkError = false
  }

  static fromResponse(response, data) {
    const message = data?.error || data?.message || `Request failed with status ${response.status}`
    const error = new ApiError(message, response.status, data?.code, data)
    return error
  }

  static networkError(originalError) {
    const error = new ApiError('Network error. Please check your connection.', 0)
    error.isNetworkError = true
    error.originalError = originalError
    return error
  }
}

// Session storage for trainer data and auth token
function getStoredTrainer() {
  try {
    const data = sessionStorage.getItem('forge_trainer')
    return data ? JSON.parse(data) : null
  } catch {
    return null
  }
}

function setStoredTrainer(trainer) {
  if (trainer) {
    sessionStorage.setItem('forge_trainer', JSON.stringify(trainer))
  } else {
    sessionStorage.removeItem('forge_trainer')
  }
}

function getStoredToken() {
  return sessionStorage.getItem('forge_trainer_token')
}

function setStoredToken(token) {
  if (token) {
    sessionStorage.setItem('forge_trainer_token', token)
  } else {
    sessionStorage.removeItem('forge_trainer_token')
  }
}

function clearAuth() {
  sessionStorage.removeItem('forge_trainer')
  sessionStorage.removeItem('forge_trainer_token')
}

/**
 * Core fetch wrapper with error handling, auth, and retry logic
 */
async function fetchWithAuth(url, options = {}, retries = 3) {
  const token = getStoredToken()

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  let response
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      response = await fetch(url, {
        ...options,
        headers,
      })
      break // Success, exit retry loop
    } catch (err) {
      if (attempt < retries) {
        // Exponential backoff: 1s, 2s, 4s
        await new Promise(r => setTimeout(r, Math.pow(2, attempt) * 1000))
        continue
      }
      throw ApiError.networkError(err)
    }
  }

  // Check response status BEFORE attempting to parse JSON
  if (!response.ok) {
    // Force redirect to login on 401 Unauthorized
    if (response.status === 401) {
      clearAuth()
      window.location.href = '#/login'
      window.location.reload()
    }
    // Try to parse error body for details, but don't crash if it's not JSON
    let data
    try {
      data = await response.json()
    } catch {
      throw new ApiError(`Request failed with status ${response.status}`, response.status)
    }
    throw ApiError.fromResponse(response, data)
  }

  // Response is OK — parse JSON body
  let data
  try {
    data = await response.json()
  } catch {
    // Response is not JSON but was successful
    return {}
  }

  return data
}

/**
 * API request with token-based authentication
 */
async function apiRequest(action, options = {}) {
  const url = `${API_BASE}?action=${action}`
  return fetchWithAuth(url, options)
}

/**
 * REST API request with token-based authentication
 */
async function restRequest(entity, options = {}) {
  const url = `${REST_BASE}/${entity}${options.id ? `/${options.id}` : ''}.json`
  return fetchWithAuth(url, {
    method: options.method || 'GET',
    body: options.body ? JSON.stringify(options.body) : undefined,
    headers: options.headers,
  })
}

/**
 * REST action request (e.g., /entity/{id}/action.json)
 */
async function restActionRequest(entity, id, action, options = {}) {
  let url = `${REST_BASE}/${entity}/${id}/${action}.json`
  if (options.queryParams) {
    const params = new URLSearchParams(options.queryParams)
    url += `?${params.toString()}`
  }
  return fetchWithAuth(url, {
    method: options.method || 'POST',
    body: options.body ? JSON.stringify(options.body) : undefined,
    headers: options.headers,
  })
}

// Auth API
export const auth = {
  async login(email, password) {
    const data = await apiRequest('trainerLogin', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    if (data.success) {
      setStoredTrainer(data.trainer)
      // Store auth token for subsequent requests
      if (data.token) {
        setStoredToken(data.token)
      }
    }
    return data
  },

  async getSession() {
    // If no token stored, return null immediately
    if (!getStoredToken()) {
      return null
    }

    try {
      const data = await apiRequest('trainerSession')
      if (data.success) {
        setStoredTrainer(data.trainer)
        return data
      }
      return null
    } catch (error) {
      clearAuth()
      return null
    }
  },

  async logout() {
    try {
      await apiRequest('trainerLogout', { method: 'POST' })
    } finally {
      clearAuth()
    }
  },

  getStoredTrainer,
  clearAuth,

  async getChatToken() {
    const data = await apiRequest('trainerChatToken')
    return data.chatToken
  },
}

// Dashboard API
export const dashboard = {
  async getData() {
    return apiRequest('trainerDashboard')
  },

  async getRecentPhotoUpdates() {
    return apiRequest('recentPhotoUpdates')
  },
}

// Daily Briefing API
export const briefing = {
  async get() {
    return apiRequest('dailyBriefing')
  },
}

// Clients API
export const clients = {
  async list() {
    return restRequest('clients')
  },

  async get(id) {
    return restRequest('clients', { id })
  },

  async create(data) {
    return restRequest('clients', {
      method: 'POST',
      body: data,
    })
  },

  async update(id, data) {
    return restRequest('clients', {
      id,
      method: 'PUT',
      body: data,
    })
  },

  async delete(id) {
    return restRequest('clients', {
      id,
      method: 'DELETE',
    })
  },

  // Generate a magic link for client portal access
  async generatePortalLink(clientId) {
    return apiRequest('generatePortalLink', {
      method: 'POST',
      body: JSON.stringify({ clientId }),
    })
  },

  // Send portal invite (magic link) via email or SMS
  async sendPortalInvite(clientId, method = 'email') {
    return apiRequest('sendPortalInvite', {
      method: 'POST',
      body: JSON.stringify({ clientId, method }),
    })
  },

  // Reset onboarding for a client
  async resetOnboarding(clientId) {
    return apiRequest('resetOnboarding', {
      method: 'POST',
      body: JSON.stringify({ clientId }),
    })
  },

  // Get client's progress photos
  async getPhotos(clientId) {
    return apiRequest(`clientPhotos&clientId=${clientId}`)
  },

  // Get all media (photos, meal photos, chat images) for a client
  async getMedia(clientId, type = null) {
    let action = `clientMedia&clientId=${clientId}`
    if (type) action += `&type=${type}`
    return apiRequest(action)
  },

  // Export a client progress report as PDF
  async exportProgressReport(id, days = 30) {
    const token = getStoredToken()
    const url = `${REST_BASE}/clients/${id}/progressReport?days=${days}`
    const response = await fetch(url, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new ApiError('Failed to export progress report', response.status)
    const blob = await response.blob()
    const downloadUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = `progress-report.pdf`
    a.click()
    URL.revokeObjectURL(downloadUrl)
  },
}

// Exercises API
export const exercises = {
  async list() {
    return restRequest('exercises')
  },

  async get(id) {
    return restRequest('exercises', { id })
  },

  async create(data) {
    return restRequest('exercises', {
      method: 'POST',
      body: data,
    })
  },

  async update(id, data) {
    return restRequest('exercises', {
      id,
      method: 'PUT',
      body: data,
    })
  },

  async delete(id) {
    return restRequest('exercises', {
      id,
      method: 'DELETE',
    })
  },
}

// Workout Templates API
export const workouts = {
  async list() {
    return restRequest('workoutTemplates')
  },

  async get(id) {
    return restRequest('workoutTemplates', { id })
  },

  async create(data) {
    return restRequest('workoutTemplates', {
      method: 'POST',
      body: data,
    })
  },

  async update(id, data) {
    return restRequest('workoutTemplates', {
      id,
      method: 'PUT',
      body: data,
    })
  },

  async delete(id) {
    return restRequest('workoutTemplates', {
      id,
      method: 'DELETE',
    })
  },

  // Exercise management within a workout template
  async addExercise(templateId, exerciseData) {
    return restActionRequest('workoutTemplates', templateId, 'addExercise', {
      method: 'POST',
      body: exerciseData,
    })
  },

  async removeExercise(templateId, exerciseItemId) {
    return restActionRequest('workoutTemplates', templateId, 'removeExercise', {
      method: 'DELETE',
      queryParams: { exerciseItemId },
    })
  },

  async updateExercise(templateId, exerciseData) {
    return restActionRequest('workoutTemplates', templateId, 'updateExercise', {
      method: 'PUT',
      body: exerciseData,
    })
  },

  // Assign workout to a client
  async assign(templateId, assignData) {
    return restActionRequest('workoutTemplates', templateId, 'assign', {
      method: 'POST',
      body: assignData,
    })
  },
}

// Knowledge Base API
export const knowledge = {
  async list() {
    return restRequest('trainerKnowledges')
  },

  async get(id) {
    return restRequest('trainerKnowledges', { id })
  },

  async create(data) {
    return restRequest('trainerKnowledges', {
      method: 'POST',
      body: data,
    })
  },

  async update(id, data) {
    return restRequest('trainerKnowledges', {
      id,
      method: 'PUT',
      body: data,
    })
  },

  async delete(id) {
    return restRequest('trainerKnowledges', {
      id,
      method: 'DELETE',
    })
  },

  async history(id) {
    return apiRequest(`knowledgeHistory&id=${id}`)
  },

  async version(versionId) {
    return apiRequest(`knowledgeVersion&id=${versionId}`)
  },

  async rollback(id, versionId) {
    return apiRequest('rollbackKnowledge', {
      method: 'POST',
      body: JSON.stringify({ id, versionId }),
    })
  },
}

// Conversations API (trainer's own conversations)
export const conversations = {
  async list() {
    return apiRequest('trainerConversations')
  },

  async get(id) {
    return apiRequest(`trainerConversation&id=${id}`)
  },
}

// Client Chat Activity (aggregated feed)
export const chatActivity = {
  async get(days = 3) {
    return apiRequest(`clientChatActivity&days=${days}`)
  },
}

// Quality Flags (auto-flagged AI messages)
export const qualityFlags = {
  async get(days = 7) {
    return apiRequest(`qualityFlags&days=${days}`)
  },
}

// Client Conversations API (for trainer to review client chats)
export const clientConversations = {
  async list(clientId = null) {
    const action = clientId
      ? `clientConversations&clientId=${clientId}`
      : 'clientConversations'
    return apiRequest(action)
  },

  async get(id) {
    return apiRequest(`clientConversation&id=${id}`)
  },

  async reply(conversationId, content) {
    return apiRequest('trainerReply', {
      method: 'POST',
      body: JSON.stringify({ conversationId, content }),
    })
  },

  async isClientOnline(clientId) {
    return apiRequest(`isClientOnline&clientId=${clientId}`)
  },

  async unreadCount() {
    return apiRequest('unreadConversationCount')
  },

  async correctMessage(messageId, correctionNote, options = {}) {
    return apiRequest('correctMessage', {
      method: 'POST',
      body: JSON.stringify({ messageId, correctionNote, ...options }),
    })
  },

  async flagMessage(messageId) {
    return apiRequest('flagMessage', {
      method: 'POST',
      body: JSON.stringify({ messageId }),
    })
  },
}

// Client Directives API (per-client)
export const clientDirectives = {
  async list(clientId) {
    return apiRequest(`clientDirectives&clientId=${clientId}`)
  },

  async create(clientId, directive, category = 'general', expiresAt = null) {
    const body = { clientId, directive, category }
    if (expiresAt) body.expiresAt = expiresAt
    return apiRequest('createClientDirective', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
}

// Escalations API
export const escalations = {
  async list(showAll = false) {
    let action = 'escalations'
    if (showAll) action += '&all=true'
    return apiRequest(action)
  },

  async count() {
    return apiRequest('escalationCount')
  },

  async resolve(id, note = null) {
    return apiRequest('resolveEscalation', {
      method: 'POST',
      body: JSON.stringify({ id, note }),
    })
  },
}

// Client Stats API (for trainer to view/update client stats)
export const clientStats = {
  async list(clientId) {
    return apiRequest(`clientStats&clientId=${clientId}`)
  },

  async create(clientId, data) {
    return apiRequest('createClientStats', {
      method: 'POST',
      body: JSON.stringify({ clientId, ...data }),
    })
  },

  async delete(id) {
    return apiRequest('deleteClientStats', {
      method: 'POST',
      body: JSON.stringify({ id }),
    })
  },
}

// Recovery Tracking API (trainer view)
export const clientRecovery = {
  async list(clientId, days = 14) {
    return apiRequest(`trainerClientRecovery&clientId=${clientId}&days=${days}`)
  },
}

// Feature Requests API
export const featureRequests = {
  async list() {
    return apiRequest('featureRequests')
  },

  async create(data) {
    return apiRequest('createFeatureRequest', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getCategories() {
    return apiRequest('featureRequestCategories')
  },

  async delete(id) {
    return apiRequest('deleteFeatureRequest', {
      method: 'POST',
      body: JSON.stringify({ id }),
    })
  },
}

// Meal Plans API
export const mealPlans = {
  async list() {
    return restRequest('mealPlans')
  },

  async get(id) {
    return restRequest('mealPlans', { id })
  },

  async create(data) {
    return restRequest('mealPlans', {
      method: 'POST',
      body: data,
    })
  },

  async update(id, data) {
    return restRequest('mealPlans', {
      id,
      method: 'PUT',
      body: data,
    })
  },

  async delete(id) {
    return restRequest('mealPlans', {
      id,
      method: 'DELETE',
    })
  },

  // Add a meal to a plan
  async addMeal(planId, mealData) {
    return restActionRequest('mealPlans', planId, 'addMeal', {
      method: 'POST',
      body: mealData,
    })
  },

  // Remove a meal from a plan
  async removeMeal(planId, mealId) {
    return restActionRequest('mealPlans', planId, 'removeMeal', {
      method: 'DELETE',
      queryParams: { mealId },
    })
  },

  // Update a meal in a plan
  async updateMeal(planId, mealData) {
    return restActionRequest('mealPlans', planId, 'updateMeal', {
      method: 'PUT',
      body: mealData,
    })
  },

  // Export a meal plan as PDF
  async exportPdf(id) {
    const token = getStoredToken()
    const url = `${REST_BASE}/mealPlans/${id}/exportPdf`
    const response = await fetch(url, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new ApiError('Failed to export PDF', response.status)
    const blob = await response.blob()
    const downloadUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = `meal-plan.pdf`
    a.click()
    URL.revokeObjectURL(downloadUrl)
  },

  // Assign a meal plan to a client
  async assignToClient(planId, clientId, startDate) {
    return apiRequest('assignMealPlan', {
      method: 'POST',
      body: JSON.stringify({ planId, clientId, startDate }),
    })
  },
}

// Training Sessions API
export const sessions = {
  async list(params = {}) {
    const queryParams = new URLSearchParams()
    if (params.start) queryParams.append('start', params.start)
    if (params.end) queryParams.append('end', params.end)
    if (params.clientId) queryParams.append('clientId', params.clientId)
    const queryString = queryParams.toString()
    const url = `${REST_BASE}/trainingSessions.json${queryString ? '?' + queryString : ''}`
    return fetchWithAuth(url)
  },

  async get(id) {
    return restRequest('trainingSessions', { id })
  },

  async create(clientId, data) {
    const params = new URLSearchParams({ clientId })
    const url = `${REST_BASE}/trainingSessions.json?${params.toString()}`
    return fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async update(id, data) {
    return restRequest('trainingSessions', {
      id,
      method: 'PUT',
      body: data,
    })
  },

  async delete(id) {
    return restRequest('trainingSessions', {
      id,
      method: 'DELETE',
    })
  },

  async cancel(id, reason) {
    return restActionRequest('trainingSessions', id, 'cancel', {
      method: 'POST',
      queryParams: reason ? { reason } : undefined,
    })
  },

  async complete(id, notes) {
    return restActionRequest('trainingSessions', id, 'complete', {
      method: 'POST',
      queryParams: notes ? { notes } : undefined,
    })
  },

  async noShow(id) {
    return restActionRequest('trainingSessions', id, 'noShow', {
      method: 'POST',
    })
  },

  async cancelSeries(id) {
    return restActionRequest('trainingSessions', id, 'cancelSeries', { method: 'POST' })
  },

  async today() {
    const url = `${REST_BASE}/trainingSessions/today.json`
    return fetchWithAuth(url)
  },

  async upcoming() {
    const url = `${REST_BASE}/trainingSessions/upcoming.json`
    return fetchWithAuth(url)
  },
}

// Text-to-Speech API
export const tts = {
  // Convert text to speech using ElevenLabs
  async elevenlabs(text, voiceId = null) {
    const body = { text }
    if (voiceId) body.voiceId = voiceId
    return apiRequest('textToSpeech', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
}

// AI Tasks API
export const tasks = {
  // List tasks (optional: status=pending|completed|overdue, clientId)
  async list(status = null, clientId = null) {
    let action = 'tasks'
    const params = []
    if (status) params.push(`status=${status}`)
    if (clientId) params.push(`clientId=${clientId}`)
    if (params.length) action += '&' + params.join('&')
    return apiRequest(action)
  },

  // Create a new task
  async create(task, clientId = null, dueDate = null, notes = null) {
    const body = { task }
    if (clientId) body.clientId = clientId
    if (dueDate) body.dueDate = dueDate
    if (notes) body.notes = notes
    return apiRequest('createTask', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  // Mark task as completed
  async complete(id) {
    return apiRequest('completeTask', {
      method: 'POST',
      body: JSON.stringify({ id }),
    })
  },

  // Delete a task
  async delete(id) {
    return apiRequest('deleteTask', {
      method: 'POST',
      body: JSON.stringify({ id }),
    })
  },
}

// Trainer Directives API
export const directives = {
  // List directives (optional: all=true to include inactive)
  async list(showAll = false) {
    let action = 'directives'
    if (showAll) action += '&all=true'
    return apiRequest(action)
  },

  // Create a new directive
  async create(directive, priority = 0, scope = 'client') {
    return apiRequest('createDirective', {
      method: 'POST',
      body: JSON.stringify({ directive, priority, scope }),
    })
  },

  // Update a directive
  async update(id, updates) {
    return apiRequest('updateDirective', {
      method: 'POST',
      body: JSON.stringify({ id, ...updates }),
    })
  },

  // Deactivate a directive (directives cannot be permanently deleted)
  async deactivate(id) {
    return apiRequest('updateDirective', {
      method: 'POST',
      body: JSON.stringify({ id, isActive: false }),
    })
  },

  async history(id) {
    return apiRequest(`directiveHistory&id=${id}`)
  },

  async version(versionId) {
    return apiRequest(`directiveVersion&id=${versionId}`)
  },

  async rollback(id, versionId) {
    return apiRequest('rollbackDirective', {
      method: 'POST',
      body: JSON.stringify({ id, versionId }),
    })
  },
}

// Coaching Notes API
export const coachingNotes = {
  async list(clientId) {
    return apiRequest(`coachingNotes&clientId=${clientId}`)
  },

  async create(clientId, content, noteType = 'general') {
    return apiRequest('createCoachingNote', {
      method: 'POST',
      body: JSON.stringify({ clientId, content, noteType }),
    })
  },

  // REST-based CRUD (alternative to above)
  async listAll(clientId = null) {
    const url = clientId
      ? `${REST_BASE}/coachingNotes.json?clientId=${clientId}`
      : `${REST_BASE}/coachingNotes.json`
    return fetchWithAuth(url)
  },

  async update(id, data) {
    return restRequest('coachingNotes', {
      id,
      method: 'PUT',
      body: data,
    })
  },

  async deactivate(id) {
    return restActionRequest('coachingNotes', id, 'deactivate', {
      method: 'POST',
    })
  },
}

// Dashboard Summary API
export const dashboardSummary = {
  async get() {
    return apiRequest('dashboardSummary')
  },
}

// Workout Logs API (trainer viewing client logs)
export const workoutLogs = {
  async list(clientId, days = 30) {
    return apiRequest(`clientWorkoutLogs&clientId=${clientId}&days=${days}`)
  },
}

// Workout Assignment API
export const workoutAssignment = {
  async assign(templateId, clientId, scheduledDate, notes = null) {
    return apiRequest('assignWorkout', {
      method: 'POST',
      body: JSON.stringify({ templateId, clientId, scheduledDate, notes }),
    })
  },
}

// Assigned Workouts API (view + mark missed)
export const assignments = {
  async list(clientId, days = 60) {
    return apiRequest(`clientAssignments&clientId=${clientId}&days=${days}`)
  },
  async markMissed(id, undo = false) {
    return apiRequest('markWorkoutMissed', {
      method: 'POST',
      body: JSON.stringify({ id, undo }),
    })
  },
}

// AI Tests API (async — start job, poll for results)
export const aiTests = {
  async run(scenarioId = null) {
    const body = scenarioId ? { scenarioId } : {}
    const { jobId } = await apiRequest('runAiTests', {
      method: 'POST',
      body: JSON.stringify(body),
    })
    return pollTestJob(jobId)
  },

  async start(scenarioId = null) {
    const body = scenarioId ? { scenarioId } : {}
    return apiRequest('runAiTests', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  async status(jobId) {
    return apiRequest(`aiTestStatus&jobId=${jobId}`)
  },

  async list() {
    return apiRequest('listAiTests')
  },
}

async function pollTestJob(jobId, maxWait = 180000) {
  const start = Date.now()
  while (Date.now() - start < maxWait) {
    await new Promise((r) => setTimeout(r, 2000))
    const result = await aiTests.status(jobId)
    if (result.status === 'complete' || result.status === 'error') {
      return result
    }
  }
  throw new Error('Test timed out after ' + (maxWait / 1000) + 's')
}

// Release Logs API
export const releaseLogs = {
  async list() {
    return apiRequest('releaseLogs')
  },
}

// Announcements / Broadcast API
export const announcements = {
  async create(data) {
    return apiRequest('createAnnouncement', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
  async list() {
    return apiRequest('listAnnouncements')
  },
  async getReleaseLogs() {
    return apiRequest('releaseLogs')
  },
}

// Client Memory API
export const memories = {
  // List memories for a client
  async list(clientId, options = {}) {
    let action = `clientMemories&clientId=${clientId}`
    if (options.type) action += `&type=${options.type}`
    if (options.includeArchived) action += '&includeArchived=true'
    return apiRequest(action)
  },

  // List recent memories across ALL clients (memory log)
  async recent(options = {}) {
    let action = 'recentMemories'
    if (options.limit) action += `&limit=${options.limit}`
    if (options.type) action += `&type=${options.type}`
    if (options.days) action += `&days=${options.days}`
    if (options.includeArchived) action += '&includeArchived=true'
    return apiRequest(action)
  },

  // Get memory statistics for a client
  async stats(clientId) {
    return apiRequest(`memoryStats&clientId=${clientId}`)
  },

  // Update a memory (pin/unpin, mark critical, edit content)
  async update(id, updates) {
    return apiRequest('updateMemory', {
      method: 'POST',
      body: JSON.stringify({ id, ...updates }),
    })
  },

  // Delete (archive) a memory
  async delete(id) {
    return apiRequest('deleteMemory', {
      method: 'POST',
      body: JSON.stringify({ id }),
    })
  },

  // Restore an archived memory
  async restore(id) {
    return apiRequest('restoreMemory', {
      method: 'POST',
      body: JSON.stringify({ id }),
    })
  },

  // Run memory consolidation (decay, archive, expire)
  async consolidate() {
    return apiRequest('consolidateMemories', {
      method: 'POST',
    })
  },
}

// Billing API
export const billing = {
  async list(params = {}) {
    const queryParts = []
    if (params.clientId) queryParts.push(`clientId=${params.clientId}`)
    if (params.status) queryParts.push(`status=${params.status}`)
    const extra = queryParts.length ? '&' + queryParts.join('&') : ''
    return apiRequest(`billingPackages${extra}`)
  },

  async get(id) {
    return apiRequest(`billingPackage&id=${id}`)
  },

  async create(data) {
    return apiRequest('createBillingPackage', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async sendInvoice(packageId) {
    return apiRequest('sendInvoice', {
      method: 'POST',
      body: JSON.stringify({ packageId }),
    })
  },

  async cancel(packageId) {
    return apiRequest('cancelBillingPackage', {
      method: 'POST',
      body: JSON.stringify({ packageId }),
    })
  },

  async markPaid(packageId, note = null) {
    const body = { packageId }
    if (note) body.note = note
    return apiRequest('markPackagePaid', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  async summary() {
    return apiRequest('billingSummary')
  },

  async outstandingCount() {
    return apiRequest('outstandingInvoiceCount')
  },
}

// Subscription Plans API
export const subscriptions = {
  async listPlans() { return apiRequest('subscriptionPlans') },
  async createPlan(data) { return apiRequest('createSubscriptionPlan', { method: 'POST', body: JSON.stringify(data) }) },
  async updatePlan(data) { return apiRequest('updateSubscriptionPlan', { method: 'POST', body: JSON.stringify(data) }) },
  async clientStatus(clientId) { return apiRequest(`clientSubscriptionStatus&clientId=${clientId}`) },
  async pause(clientId) { return apiRequest('pauseClientSubscription', { method: 'POST', body: JSON.stringify({ clientId }) }) },
  async resume(clientId) { return apiRequest('resumeClientSubscription', { method: 'POST', body: JSON.stringify({ clientId }) }) },
  async cancel(clientId) { return apiRequest('cancelClientSubscription', { method: 'POST', body: JSON.stringify({ clientId }) }) },
  async assign(clientId, planId, status = 'ACTIVE') { return apiRequest('assignClientSubscription', { method: 'POST', body: JSON.stringify({ clientId, planId, status }) }) },
}

// Trainer Settings (SMS Alerts)
export const trainerSettings = {
  async get() {
    return apiRequest('getTrainerSettings')
  },

  async update(settings) {
    return apiRequest('updateTrainerSettings', {
      method: 'POST',
      body: JSON.stringify(settings),
    })
  },

  async updateProfile(profile) {
    const data = await apiRequest('updateTrainerProfile', {
      method: 'POST',
      body: JSON.stringify(profile),
    })
    if (data.trainer) {
      setStoredTrainer(data.trainer)
    }
    return data
  },

  async sendTestSms() {
    return apiRequest('sendTestSms', {
      method: 'POST',
    })
  },
}

// Export utils for advanced usage
export { clearAuth, getStoredToken }

export default {
  auth,
  dashboard,
  briefing,
  dashboardSummary,
  clients,
  exercises,
  workouts,
  knowledge,
  conversations,
  clientConversations,
  clientDirectives,
  clientStats,
  clientRecovery,
  featureRequests,
  mealPlans,
  sessions,
  tts,
  tasks,
  directives,
  escalations,
  memories,
  coachingNotes,
  workoutLogs,
  workoutAssignment,
  billing,
  subscriptions,
  trainerSettings,
  aiTests,
  qualityFlags,
  ApiError,
}
